import http, { handleHttpError, handleHttpResponse } from '../http'
import { HTTPResponse } from '../http/http.types'
import { IAggregatedDataResponse } from './data.types'
import { IEquipment } from '@/pages/Equipment/equipment.types.ts'
import { IParameter } from '@/pages/Parameters/parameters.types.ts'

interface IAggregatedDataParams {
  deviceId: IEquipment['id']
  protocolId: IEquipment['protocolId']
  parameters: Array<IParameter['sourceId']>
  fromTime: number
  toTime: number
  // Кол-во точек
  aggregationFactor: number
}

const getAggregated = ({
  deviceId,
  parameters,
  protocolId,
  ...params
}: IAggregatedDataParams): Promise<HTTPResponse<IAggregatedDataResponse>> => {
  const url =
    protocolId === 'lkds'
      ? `/ts-storage/v2/lkds/${deviceId}/parameters/data`
      : `/ts-storage/v1/${deviceId}/parameters/data`

  return http
    .get(url, {
      params: {
        ...params,
        parameters: parameters.join(','),
        fromTime: params.fromTime * 1000 * 1000,
        toTime: params.toTime * 1000 * 1000,
      },
    })
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

export type TLastValuesResponse = Record<IEquipment['id'], Record<IParameter['id'], number>>
interface IGetLastValuesParams {
  deviceId: IEquipment['id']
  parameters: Array<IParameter['sourceId']>
  sourceIdToIdMap: Record<IParameter['sourceId'], IParameter['id']>
  protocolId?: IEquipment['protocolId']
}

const getLastValues = async (params: IGetLastValuesParams): Promise<HTTPResponse<TLastValuesResponse>> => {
  const url =
    params.protocolId === 'lkds'
      ? `/ts-storage/v2/lkds/${params.deviceId}/parameters/last`
      : `/ts-storage/v1/${params.deviceId}/parameters/last`

  const resp: HTTPResponse<IAggregatedDataResponse> = await http
    .get(url, { params: { parameters: params.parameters.join(',') } })
    .then(handleHttpResponse)
    .catch(handleHttpError)

  if (resp.status === 'error') {
    return resp
  }

  const result = {}
  for (const ts of resp.body.timestamps) {
    for (const value of ts.values) {
      result[params.sourceIdToIdMap[value.name]] = value.value
    }
  }

  return {
    status: 'success',
    body: {
      [params.deviceId]: result,
    },
  }
}

/**
 * Получить состояние в прошлом (последние значения указанных параметров в указанный момент времени)
 * */

interface IGetLastValuesInPastParams {
  protocolId?: IEquipment['protocolId']
  deviceId: IEquipment['id']
  parameters: Array<IParameter['sourceId']>
  sourceIdToIdMap: Record<IParameter['sourceId'], IParameter['id']>
  ts: number
}

const getLastValuesInPast = async ({
  sourceIdToIdMap,
  ...params
}: IGetLastValuesInPastParams): Promise<HTTPResponse<TLastValuesResponse>> => {
  const url =
    params.protocolId === 'lkds'
      ? `/ts-storage/v2/lkds/${params.deviceId}/parameters/data`
      : `/ts-storage/v1/${params.deviceId}/parameters/data`

  const resp = await http
    .get(url, {
      params: {
        ...params,
        parameters: params.parameters.join(','),
        fromTime: (params.ts - 15 * 60 * 1000) * 1000 * 1000,
        toTime: params.ts * 1000 * 1000,
      },
    })
    .then(handleHttpResponse)
    .catch(handleHttpError)

  if (resp.status === 'error') {
    return resp
  }

  const result = {}
  for (const ts of resp.body.timestamps) {
    for (const value of ts.values) {
      result[sourceIdToIdMap[value.name]] = value.value
    }
  }

  return {
    status: 'success',
    body: {
      [params.deviceId]: result,
    },
  }
}

const dataApi = { getAggregated, getLastValues, getLastValuesInPast }
export default dataApi
